import { useEffect, useMemo, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";

import { Action } from "@/constants/events";
import { useEventTracker } from "@/hooks/useEventTracker";
import useGetSearchParamValues from "@/hooks/useGetSearchParamValues";
import toggleURLSearchParamValue from "@/utils/toggleURLSearchParamValue";

import Checkbox from "@/components/botanic/Checkbox";
import ExpandableFilterOptions from "@/components/ExpandableFilterOptions";
import FilterSearch from "@/components/Filters/FilterSearch";
import TrackImpression from "@/components/TrackImpression";

import { DYNAMIC_FILTER_SECTION_LIMIT } from "../const";
import { FilterQueryStringKeyProp, NoFollowLinkProp } from "../DynamicFilters";
import LabelWithBadge from "../LabelWithBadge";
import { GroupFilter, GroupFilterValue } from "../types";

const Checkboxes: React.FC<{
  disabled?: (option: GroupFilterValue) => boolean;
  options: GroupFilterValue[];
  showCount?: boolean;
  name: string;
  noFollowLink?: NoFollowLinkProp;
  includeFilterEvents?: boolean;
  eventCategory?: string;
  filterCategory?: string;
  filterQueryStringKey?: FilterQueryStringKeyProp;
}> = ({
  options,
  name,
  disabled,
  showCount = false,
  noFollowLink,
  includeFilterEvents,
  eventCategory,
  filterCategory,
  filterQueryStringKey = (filterName) => filterName,
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const { publishEvent } = useEventTracker();
  const getSearchParamValues = useGetSearchParamValues();

  const queryStringKey = filterQueryStringKey(name);
  const selectedValues = getSearchParamValues(queryStringKey);

  const targetURLSearchParams = new URLSearchParams(searchParams?.toString());
  // * Remove pagination params when clicking fulfillment filter link.
  targetURLSearchParams.delete("page");

  return (
    <>
      {options.map((filter) => {
        const href = {
          pathname,
          query: toggleURLSearchParamValue(
            targetURLSearchParams,
            queryStringKey,
            filter.value,
          ).toString(),
        };
        const checked = selectedValues.includes(filter.value);
        const filterSectionLimitReached =
          selectedValues.length >= DYNAMIC_FILTER_SECTION_LIMIT;
        const sectionLimitTriggerForceDisable =
          !checked && filterSectionLimitReached;
        return (
          <TrackImpression
            key={`${name}_${filter.value}`}
            trackingFunction={() => {
              if (
                includeFilterEvents &&
                eventCategory &&
                filterCategory !== "Strain name"
              ) {
                publishEvent({
                  action: Action.impression,
                  category: eventCategory,
                  label: filter.label,
                });
              }
            }}
          >
            <Checkbox
              href={href}
              id={`${name}_${filter.value}`}
              data-testid={`${name}_${filter.value}`}
              disabled={
                disabled?.(filter) ||
                filter.count === 0 ||
                sectionLimitTriggerForceDisable
              }
              name={name}
              value={filter.value}
              checked={checked}
              className="px-xs py-md"
              rel={noFollowLink?.(href) ? "nofollow" : undefined}
              onChecked={
                includeFilterEvents && eventCategory
                  ? () => {
                      publishEvent({
                        action: Action.click,
                        category: eventCategory,
                        label: filter.label,
                      });
                    }
                  : undefined
              }
            >
              <LabelWithBadge
                displayName={filter.label}
                badge={filter?.badge || ""}
              />
              {showCount && (
                <span className="pl-sm text-grey">({filter.count})</span>
              )}
            </Checkbox>
          </TrackImpression>
        );
      })}
    </>
  );
};

interface Props {
  filter: GroupFilter;
  disabled?: (option: GroupFilterValue) => boolean;
  optionTake?: number;
  noFollowLink?: NoFollowLinkProp;
  includeFilterEvents?: boolean;
  eventCategory?: string;
  filterCategory?: string;
  filterQueryStringKey?: FilterQueryStringKeyProp;
}

const CheckboxSearchList = ({
  disabled,
  filter,
  optionTake = 7,
  noFollowLink,
  includeFilterEvents,
  eventCategory,
  filterCategory,
  filterQueryStringKey = (filterName) => filterName,
}: Props) => {
  const [subFiltersResult, setSubFiltersResult] = useState(filter.values);
  const [visibleOptions, hiddenOptions] = useMemo(
    () => [
      subFiltersResult.slice(0, optionTake), // visibleOptions
      subFiltersResult.slice(optionTake), // hiddenOptions
    ],
    [subFiltersResult],
  );

  const hideFilterSearch = filter.values.length <= optionTake;
  const checkboxesProps = {
    disabled,
    eventCategory,
    filterCategory,
    filterQueryStringKey,
    includeFilterEvents,
    name: filter.name,
    noFollowLink,
    showCount: filter.showCount,
  };

  // * This effect is required in order for the list to update with new server data.
  useEffect(() => {
    setSubFiltersResult(filter.values);
  }, [filter.values]);

  return (
    <>
      {!hideFilterSearch && (
        <FilterSearch
          label={filter.label}
          options={filter.values}
          handleFilterSearch={(subFilters: GroupFilterValue[]) =>
            setSubFiltersResult(subFilters)
          }
          fuseOptions={{ keys: ["value"] }}
        />
      )}
      <div
        className="overflow-auto max-h-96"
        data-testid="filter-checkbox-list"
      >
        <Checkboxes options={visibleOptions} {...checkboxesProps} />

        {Boolean(hiddenOptions.length) && (
          <ExpandableFilterOptions>
            <Checkboxes options={hiddenOptions} {...checkboxesProps} />
          </ExpandableFilterOptions>
        )}
      </div>
    </>
  );
};

export default CheckboxSearchList;
